// @seventide/types — platform area: notifications, deadlines and "This week" (FR-11.1, FR-11.2, FR-11.3).
// Re-exported by ../platform.ts. Names are prefixed (Notification*, Deadline*, ThisWeek*) to avoid collisions.
import { z } from 'zod';
import { Id, IsoDate, IsoDateTime, paginated } from '../common';
import { APP_MODULES, AppModule, DeadlineStatus, Role, type AppModule as AppModuleT } from '../enums';

// ---------------------------------------------------------------- shared constants

/**
 * FR-11.2 priority order for modules when two actions fall due on the same day (lower index = higher
 * priority): care and health deadlines tend to have statutory consequences, platform reminders the least.
 */
export const DEADLINE_MODULE_PRIORITY: readonly AppModuleT[] = [
  'care',
  'health',
  'attorney',
  'gifts',
  'money',
  'documents',
  'workspace',
  'platform',
] as const;

/** Plain-English labels for the areas shown on This week and in notification settings. */
export const NOTIFICATION_MODULE_LABELS: Record<AppModuleT, string> = {
  care: 'Care finances',
  health: 'Health evidence',
  attorney: 'Attorney records',
  gifts: 'Gifts',
  money: 'Money',
  documents: 'Documents',
  workspace: 'Family and profile',
  platform: 'Subscription and reminders',
};

/** Deadline kinds created by the platform engine for billing; only the owner (billing) can act on these. */
export const BILLING_DEADLINE_KINDS = ['subscription_renewal', 'trial_ending'] as const;

/** Default "This week" window and its maximum (days ahead). */
export const THIS_WEEK_DEFAULT_DAYS = 7;
export const THIS_WEEK_MAX_DAYS = 90;
/** Maximum snooze length (days). */
export const DEADLINE_MAX_SNOOZE_DAYS = 366;

// ---------------------------------------------------------------- notifications

const BoolQuery = z
  .enum(['true', 'false', '1', '0'])
  .optional()
  .transform((v) => v === 'true' || v === '1');

export const NotificationDto = z.object({
  id: Id,
  module: AppModule,
  kind: z.string(),
  /** Generic in-app title (never amounts or health detail). */
  title: z.string(),
  /** In-app path, e.g. `/w/<id>/this-week`. */
  link: z.string().nullable(),
  entityType: z.string().nullable(),
  entityId: Id.nullable(),
  deadlineId: Id.nullable(),
  readAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
});
export type NotificationDto = z.infer<typeof NotificationDto>;

export const NotificationListQuery = z.object({
  /** `true` → unread only. */
  unread: BoolQuery,
  limit: z.coerce.number().int().min(1).max(100).default(20),
  offset: z.coerce.number().int().min(0).default(0),
});
export type NotificationListQuery = z.input<typeof NotificationListQuery>;

export const NotificationList = paginated(NotificationDto).extend({ unreadCount: z.number().int() });
export type NotificationList = z.infer<typeof NotificationList>;

export const NotificationUnreadCount = z.object({ count: z.number().int() });
export type NotificationUnreadCount = z.infer<typeof NotificationUnreadCount>;

export const NotificationParams = z.object({ notificationId: Id });

export const NotificationMarkAllReadResponse = z.object({ updated: z.number().int() });
export type NotificationMarkAllReadResponse = z.infer<typeof NotificationMarkAllReadResponse>;

// ---------------------------------------------------------------- preferences

export const NOTIFICATION_DIGESTS = ['immediate', 'daily', 'weekly'] as const;
export const NotificationDigest = z.enum(NOTIFICATION_DIGESTS);
export type NotificationDigest = z.infer<typeof NotificationDigest>;
export const NOTIFICATION_DIGEST_LABELS: Record<NotificationDigest, string> = {
  immediate: 'As things happen',
  daily: 'At most once a day',
  weekly: 'At most once a week',
};

const Hour = z.number().int().min(0).max(23);

export const NotificationQuietHours = z
  .object({ startHour: Hour, endHour: Hour })
  .refine((q) => q.startHour !== q.endHour, {
    message: 'Quiet hours must start and end at different times',
    path: ['endHour'],
  });
export type NotificationQuietHours = z.infer<typeof NotificationQuietHours>;

export const NotificationPreferencesDto = z.object({
  emailEnabled: z.boolean(),
  pushEnabled: z.boolean(),
  digest: NotificationDigest,
  /** UK local time (Europe/London). null = no quiet hours. */
  quietHours: NotificationQuietHours.nullable(),
  /** Areas the person does not want emails or phone notifications about (in-app still shows them). */
  mutedModules: z.array(AppModule),
  /** False until the person has saved preferences for this workspace (defaults apply). */
  saved: z.boolean(),
});
export type NotificationPreferencesDto = z.infer<typeof NotificationPreferencesDto>;

export const UpdateNotificationPreferencesRequest = z.object({
  emailEnabled: z.boolean(),
  pushEnabled: z.boolean(),
  digest: NotificationDigest,
  quietHours: NotificationQuietHours.nullable(),
  mutedModules: z
    .array(AppModule)
    .max(APP_MODULES.length)
    .transform((m) => [...new Set(m)]),
});
export type UpdateNotificationPreferencesRequest = z.input<typeof UpdateNotificationPreferencesRequest>;

export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferencesDto = {
  emailEnabled: true,
  pushEnabled: true,
  digest: 'immediate',
  quietHours: null,
  mutedModules: [],
  saved: false,
};

// ---------------------------------------------------------------- This week (FR-11.2) and deadline actions

export const ThisWeekQuery = z.object({
  days: z.coerce.number().int().min(1).max(THIS_WEEK_MAX_DAYS).default(THIS_WEEK_DEFAULT_DAYS),
});
export type ThisWeekQuery = z.input<typeof ThisWeekQuery>;

export const ThisWeekItem = z.object({
  id: Id,
  module: AppModule,
  kind: z.string(),
  title: z.string(),
  dueAt: IsoDateTime,
  /** UK calendar date of `dueAt`. */
  dueDate: IsoDate,
  status: DeadlineStatus,
  overdue: z.boolean(),
  /** Whole UK calendar days from today (negative = overdue, 0 = today). */
  daysUntilDue: z.number().int(),
  snoozedUntil: IsoDateTime.nullable(),
  ownerUserId: Id.nullable(),
  ownerName: z.string().nullable(),
  link: z.string().nullable(),
  /** The caller may complete / snooze / dismiss / assign this item (role, module, read-only, archive). */
  canAct: z.boolean(),
});
export type ThisWeekItem = z.infer<typeof ThisWeekItem>;

export const ThisWeekResponse = z.object({
  /** UK date the list was computed for. */
  today: IsoDate,
  days: z.number().int(),
  /** Priority order: overdue first, then due date, then module priority. */
  items: z.array(ThisWeekItem),
  overdueCount: z.number().int(),
  dueSoonCount: z.number().int(),
  /** Open items due after the window. */
  laterCount: z.number().int(),
});
export type ThisWeekResponse = z.infer<typeof ThisWeekResponse>;

export const DeadlineParams = z.object({ deadlineId: Id });

export const DeadlineSnoozeRequest = z.object({
  /** ISO date (snoozes until 09:00 UK time that day) or ISO timestamp. Must be in the future, max a year. */
  until: z.union([IsoDate, IsoDateTime]),
});
export type DeadlineSnoozeRequest = z.infer<typeof DeadlineSnoozeRequest>;

export const DeadlineAssignRequest = z.object({ ownerUserId: Id.nullable() });
export type DeadlineAssignRequest = z.infer<typeof DeadlineAssignRequest>;

export const DeadlineAssignee = z.object({
  userId: Id,
  name: z.string(),
  role: Role,
});
export type DeadlineAssignee = z.infer<typeof DeadlineAssignee>;
export const DeadlineAssigneeList = z.object({ items: z.array(DeadlineAssignee) });
export type DeadlineAssigneeList = z.infer<typeof DeadlineAssigneeList>;

/** Every deadline action returns the updated item. */
export const DeadlineActionResponse = ThisWeekItem;
export type DeadlineActionResponse = ThisWeekItem;

// ---------------------------------------------------------------- pure helpers shared by API and web

/** Index in DEADLINE_MODULE_PRIORITY (unknown modules sort last). */
export function deadlineModuleRank(module: string): number {
  const i = (DEADLINE_MODULE_PRIORITY as readonly string[]).indexOf(module);
  return i === -1 ? DEADLINE_MODULE_PRIORITY.length : i;
}

/** FR-11.2 ordering: overdue first → due date → module priority → title. */
export function compareThisWeekItems(
  a: Pick<ThisWeekItem, 'overdue' | 'dueAt' | 'module' | 'title'>,
  b: Pick<ThisWeekItem, 'overdue' | 'dueAt' | 'module' | 'title'>,
): number {
  if (a.overdue !== b.overdue) return a.overdue ? -1 : 1;
  const t = new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime();
  if (t !== 0) return t;
  const m = deadlineModuleRank(a.module) - deadlineModuleRank(b.module);
  if (m !== 0) return m;
  return a.title.localeCompare(b.title, 'en-GB');
}
